import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  getSubAdminsAPI,
  createSubAdminAPI,
  editSubAdminAPI,
  deleteSubAdminAPI,
} from "../../API/AdminManagementAPI";
import CustomFileDropper from "../../components/dropers/CustomFileDropper";
import Button from "../../components/button/Button";
import CancelButton from "../../components/button/CancelButton";
import ButtonLoader from "../../components/button/ButtonLoader";
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash } from "react-icons/fa";

const AdminManagement = () => {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    profileImage: "",
    permissions: {
      dashoardStatisticsPermission: false,
      userPartnershipPermission: false,
      partnershipPermission: false,
      userPermission: false,
      chatPermission: false,
      socialMediaPermission: false,
      termAndConditionPermission: false,
      faqPermission: false,
      profilePermission: false,
      merchandiseProductPermission: false,
      agreementPermission: false,
      notificationPermission: false,
      athletePointsPermission: false,
      configPermission: false,
      goalPermission: false,
      merchandiseCategoryPermission: false,
    },
  });

  const permissionLabels = {
    dashoardStatisticsPermission: "Dashboard Statistics",
    userPartnershipPermission: "User Partnerships",
    partnershipPermission: "Partnerships",
    userPermission: "Users",
    chatPermission: "Chat/Support",
    socialMediaPermission: "Social Media",
    termAndConditionPermission: "Terms & Conditions",
    faqPermission: "FAQs",
    profilePermission: "Profile",
    merchandiseProductPermission: "Merchandise Products",
    agreementPermission: "Agreements",
    notificationPermission: "Notifications",
    athletePointsPermission: "Athlete Points",
    configPermission: "System Configuration",
    goalPermission: "User Goals",
    merchandiseCategoryPermission: "Merchandise Categories",
  };

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  const fetchSubAdmins = async () => {
    try {
      setLoading(true);
      const response = await getSubAdminsAPI();
      setSubAdmins(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch sub-admins");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, admin = null) => {
    setModalMode(mode);
    if (mode === "edit" && admin) {
      setFormData({
        subAdminId: admin.subAdmin._id,
        first_name: admin.subAdmin.first_name || "",
        last_name: admin.subAdmin.last_name || "",
        email: admin.subAdmin.email || "",
        password: "",
        profileImage: admin.subAdmin.profileImage || "",
        permissions: admin.subAdminPermission || {},
      });
    } else {
      resetFormData();
    }
    setShowModal(true);
  };

  const resetFormData = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      profileImage: "",
      permissions: {
        dashoardStatisticsPermission: false,
        userPartnershipPermission: false,
        partnershipPermission: false,
        userPermission: false,
        chatPermission: false,
        socialMediaPermission: false,
        termAndConditionPermission: false,
        faqPermission: false,
        profilePermission: false,
        merchandiseProductPermission: false,
        agreementPermission: false,
        notificationPermission: false,
        athletePointsPermission: false,
        configPermission: false,
        goalPermission: false,
        merchandiseCategoryPermission: false,
      },
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetFormData();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionChange = (permission) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission],
      },
    }));
  };

  const handleFileDrop = (uploadedFilePath, type) => {
    setFormData((prev) => ({
      ...prev,
      profileImage: uploadedFilePath || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (modalMode === "create") {
        if (!formData.password) {
          toast.error("Password is required for new admins");
          setIsSubmitting(false);
          return;
        }
        const payload = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          password: formData.password,
          profileImage: formData.profileImage,
          permissions: formData.permissions,
        };
        await createSubAdminAPI(payload);
        toast.success("Sub-admin created successfully");
      } else {
        const payload = {
          subAdminId: formData.subAdminId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          profileImage: formData.profileImage,
          permissions: formData.permissions,
        };
        await editSubAdminAPI(payload);
        toast.success("Sub-admin updated successfully");
      }
      handleCloseModal();
      fetchSubAdmins();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        `Failed to ${modalMode === "create" ? "create" : "update"} sub-admin`
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (subAdminId) => {
    if (!window.confirm("Are you sure you want to delete this sub-admin?")) {
      return;
    }

    setDeletingId(subAdminId);
    try {
      await deleteSubAdminAPI(subAdminId);
      toast.success("Sub-admin deleted successfully");
      fetchSubAdmins();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete sub-admin"
      );
      console.error(error);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ButtonLoader />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Admin Management</h1>
        <Button
          text={
            <>
              <FaPlus className="inline mr-2" /> Add Sub-Admin
            </>
          }
          onClick={() => handleOpenModal("create")}
          type="button"
        />
      </div>

      {/* Sub-Admins Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Profile
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permissions Count
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subAdmins.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No sub-admins found
                </td>
              </tr>
            ) : (
              subAdmins.map((admin) => {
                const permissionCount = Object.values(
                  admin.subAdminPermission || {}
                ).filter((v) => v === true).length;
                return (
                  <tr key={admin.subAdmin._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <img
                        src={
                          admin.subAdmin.profileImage ||
                          "https://via.placeholder.com/40"
                        }
                        alt="Profile"
                        className="h-10 w-10 rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {admin.subAdmin.first_name} {admin.subAdmin.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {admin.subAdmin.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {permissionCount} permissions
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleOpenModal("edit", admin)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3 font-medium"
                        disabled={deletingId !== null}
                      >
                        <FaEdit className="inline" /> Edit
                      </button>
                      {deletingId === admin.subAdmin._id ? (
                        <span className="inline-flex items-center gap-2 text-gray-500">
                          <ButtonLoader customClass="w-4 h-4" />
                          Deleting...
                        </span>
                      ) : (
                        <button
                          onClick={() => handleDelete(admin.subAdmin._id)}
                          className="text-red-600 hover:text-red-900 font-medium"
                          disabled={deletingId !== null}
                        >
                          <FaTrash className="inline" /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {modalMode === "create" ? "Create" : "Edit"} Sub-Admin
            </h2>
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email * {modalMode === "edit" && "(Cannot be changed)"}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={modalMode === "edit"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              {modalMode === "create" && (
                <div className="mb-4 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              )}

              {/* Profile Image */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image
                </label>
                <CustomFileDropper
                  title="Click to upload profile image"
                  instructions="Upload admin profile picture (PNG, JPG, JPEG)"
                  onFileDrop={handleFileDrop}
                  type="profileImage"
                  fileTypes={["image/png", "image/jpg", "image/jpeg"]}
                  defaultFile={formData.profileImage}
                  maxFiles={1}
                />
              </div>

              {/* Permissions */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  {Object.keys(formData.permissions).map((permission) => (
                    <label
                      key={permission}
                      className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions[permission]}
                        onChange={() => handlePermissionChange(permission)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {permissionLabels[permission]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <CancelButton
                  text="Cancel"
                  onClick={handleCloseModal}
                  type="button"
                  disabled={isSubmitting}
                />
                <Button
                  text={
                    modalMode === "create"
                      ? "Create Sub-Admin"
                      : "Update Sub-Admin"
                  }
                  type="submit"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
